import type { ScrapeJob, DocumentUploadJob } from '../types'

type JobStatusPanelsProps = {
  scrapeJobs: ScrapeJob[]
  uploadJobs: DocumentUploadJob[]
  isScrapePolling: boolean
  isUploadPolling: boolean
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'badge-success'
    case 'running':
      return 'badge-primary'
    case 'failed':
      return 'badge-error'
    default:
      return 'badge-warning'
  }
}

export function JobStatusPanels({
  scrapeJobs,
  uploadJobs,
  isScrapePolling,
  isUploadPolling,
}: JobStatusPanelsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-theme-primary">Recent Scrape Jobs</h2>
          {isScrapePolling && (
            <span className="flex items-center text-xs text-theme-accent">
              <span className="w-2 h-2 bg-current rounded-full animate-pulse mr-2" />
              Updating...
            </span>
          )}
        </div>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {scrapeJobs.map((job) => (
            <div
              key={job.id}
              className="flex items-center justify-between p-3 bg-surface-elevated rounded-md"
            >
              <div>
                <span className="text-sm font-medium text-theme-primary">{job.job_type}</span>
                <span className="text-sm text-theme-muted ml-2">
                  {job.urls_processed}/{job.urls_total} URLs
                </span>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${statusBadgeClass(job.status)}`}>
                {job.status}
              </span>
            </div>
          ))}
          {scrapeJobs.length === 0 && (
            <p className="text-theme-muted text-sm">No scrape jobs yet</p>
          )}
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-theme-primary">Recent Upload Jobs</h2>
          {isUploadPolling && (
            <span className="flex items-center text-xs text-theme-accent">
              <span className="w-2 h-2 bg-current rounded-full animate-pulse mr-2" />
              Updating...
            </span>
          )}
        </div>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {uploadJobs.map((job) => (
            <div
              key={job.id}
              className="flex items-center justify-between p-3 bg-surface-elevated rounded-md"
            >
              <div>
                <span className="text-sm font-medium text-theme-primary">
                  {job.metadata?.filename || 'Document'}
                </span>
                <span className="text-sm text-theme-muted ml-2">
                  {job.chunks_created} chunks
                </span>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${statusBadgeClass(job.status)}`}>
                {job.status}
              </span>
            </div>
          ))}
          {uploadJobs.length === 0 && (
            <p className="text-theme-muted text-sm">No upload jobs yet</p>
          )}
        </div>
      </div>
    </div>
  )
}
